import { Building2, FileClock, Files, Send, ShieldCheck, Truck } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

import { ToggleTabs } from '@/components/ui/ToggleTabs'
import {
  blockingChecks,
  checksForCard,
  type FillApplicationState,
  FillCard,
  hasFilesReading,
  isRowCheck,
} from '@/models/fillApplication'
import type { Org } from '@/models/user'
import { BusinessCard } from './BusinessCard'
import { ConfirmationStep } from './ConfirmationStep'
import { CoverageCard } from './CoverageCard'
import { DocumentsStep } from './DocumentsStep'
import { EquipmentCard } from './EquipmentCard'
import { FillJumpProvider } from './FillJumpProvider'
import { FillStepFooter } from './FillStepFooter'
import { FillStepLabel } from './FillStepLabel'
import { FillStepPanel } from './FillStepPanel'
import { defaultStep, FILL_STEP_LABELS, FillStep, nextStep, previousStep, stepForCard } from './fillSteps'
import { LossRunsCard } from './LossRunsCard'
import { UnsavedEditsDialog } from './UnsavedEditsDialog'
import { useBusinessCardForm } from './useBusinessCardForm'
import { useCoverageCardForm } from './useCoverageCardForm'
import type { FillCardForm } from './useFillCardSave'
import { type SendableCard, useSendApplication } from './useSendApplication'
import { useUnsavedEditsGuard } from './useUnsavedEditsGuard'

interface FillApplicationFormProps {
  orgId: string
  organisation: Org
  state: FillApplicationState
  /** The tab to open on, when the step before decided it; else `defaultStep`. */
  initialStep?: FillStep | null
}

export function FillApplicationForm({
  orgId,
  organisation,
  state,
  initialStep = null,
}: FillApplicationFormProps) {
  const business = useBusinessCardForm(orgId, organisation)
  const coverage = useCoverageCardForm(orgId, organisation.currentInsuranceApplication)
  const [step, setStep] = useState<FillStep>(() => initialStep ?? defaultStep(state))
  const topRef = useRef<HTMLDivElement>(null)
  const showCard = useCallback((card: FillCard) => setStep(stepForCard(card)), [])
  // Tab order: Send saves them in this order and stops at the first that fails.
  const cards: SendableCard[] = [
    { card: FillCard.BUSINESS, label: 'Business', isDirty: business.isDirty, save: business.save },
    { card: FillCard.COVERAGE, label: 'Coverage', isDirty: coverage.isDirty, save: coverage.save },
  ]
  const dirtyLabels = cards.filter((card) => card.isDirty).map((card) => card.label)
  const blocker = useUnsavedEditsGuard(dirtyLabels)
  const { send, sending, error, blockedNonce } = useSendApplication(orgId, cards, { showCard })
  const blocking = blockingChecks(state.checks)
  const toFix = (card: FillCard) => checksForCard(blocking, card).length

  function goTo(next: FillStep) {
    setStep(next)
    // Continue sits at the bottom of a long tab, so the next one opens at its top.
    const top = topRef.current
    if (top && top.getBoundingClientRect().top < 0) top.scrollIntoView({ block: 'start' })
  }

  async function saveAndContinue(form: FillCardForm, from: FillStep) {
    if (form.isDirty && !(await form.save())) return
    goTo(nextStep(from))
  }

  const tabs = [
    {
      value: FillStep.DOCUMENTS,
      icon: <Files />,
      label: <FillStepLabel label={FILL_STEP_LABELS[FillStep.DOCUMENTS]} reading={hasFilesReading(state)} />,
    },
    {
      value: FillStep.BUSINESS,
      icon: <Building2 />,
      label: (
        <FillStepLabel
          label={FILL_STEP_LABELS[FillStep.BUSINESS]}
          toFix={toFix(FillCard.BUSINESS)}
          unsaved={business.isDirty}
        />
      ),
    },
    {
      value: FillStep.EQUIPMENT,
      icon: <Truck />,
      label: <FillStepLabel label={FILL_STEP_LABELS[FillStep.EQUIPMENT]} toFix={toFix(FillCard.EQUIPMENT)} />,
    },
    {
      value: FillStep.LOSS_RUNS,
      icon: <FileClock />,
      label: <FillStepLabel label={FILL_STEP_LABELS[FillStep.LOSS_RUNS]} toFix={toFix(FillCard.LOSS_RUNS)} />,
    },
    {
      value: FillStep.COVERAGE,
      icon: <ShieldCheck />,
      label: (
        <FillStepLabel
          label={FILL_STEP_LABELS[FillStep.COVERAGE]}
          toFix={toFix(FillCard.COVERAGE)}
          unsaved={coverage.isDirty}
        />
      ),
    },
    {
      value: FillStep.CONFIRMATION,
      icon: <Send />,
      label: <FillStepLabel label={FILL_STEP_LABELS[FillStep.CONFIRMATION]} />,
    },
  ]

  return (
    <FillJumpProvider onShowCard={showCard}>
      <div ref={topRef} className="scroll-mt-24">
        <ToggleTabs
          items={tabs}
          value={step}
          onValueChange={(value) => setStep(value as FillStep)}
          className="gap-6"
        >
          <FillStepPanel value={FillStep.DOCUMENTS} active={step === FillStep.DOCUMENTS}>
            <DocumentsStep orgId={orgId} state={state} onContinue={() => goTo(FillStep.BUSINESS)} />
          </FillStepPanel>
          <FillStepPanel value={FillStep.BUSINESS} active={step === FillStep.BUSINESS}>
            <BusinessCard orgId={orgId} organisation={organisation} state={state} form={business} />
            <FillStepFooter
              label="Save and continue"
              saving={business.saving}
              onBack={() => goTo(previousStep(FillStep.BUSINESS))}
              onDiscard={business.isDirty ? business.discard : undefined}
              onContinue={() => void saveAndContinue(business, FillStep.BUSINESS)}
            />
          </FillStepPanel>
          <FillStepPanel value={FillStep.EQUIPMENT} active={step === FillStep.EQUIPMENT}>
            <EquipmentCard
              orgId={orgId}
              // Each driver or truck shows what it lacks on its own row, under the roster's incomplete count.
              checks={state.checks.filter((check) => !isRowCheck(check))}
              eld={state.eld}
              owner={organisation.currentInsuranceApplication?.businessOwner}
            />
            <FillStepFooter
              label="Save and continue"
              onBack={() => goTo(previousStep(FillStep.EQUIPMENT))}
              onContinue={() => goTo(nextStep(FillStep.EQUIPMENT))}
            />
          </FillStepPanel>
          <FillStepPanel value={FillStep.LOSS_RUNS} active={step === FillStep.LOSS_RUNS}>
            <LossRunsCard orgId={orgId} state={state} />
            <FillStepFooter
              label="Save and continue"
              onBack={() => goTo(previousStep(FillStep.LOSS_RUNS))}
              onContinue={() => goTo(nextStep(FillStep.LOSS_RUNS))}
            />
          </FillStepPanel>
          <FillStepPanel value={FillStep.COVERAGE} active={step === FillStep.COVERAGE}>
            <CoverageCard checks={state.checks} form={coverage} />
            <FillStepFooter
              label="Save and continue"
              saving={coverage.saving}
              onBack={() => goTo(previousStep(FillStep.COVERAGE))}
              onDiscard={coverage.isDirty ? coverage.discard : undefined}
              onContinue={() => void saveAndContinue(coverage, FillStep.COVERAGE)}
            />
          </FillStepPanel>
          <FillStepPanel value={FillStep.CONFIRMATION} active={step === FillStep.CONFIRMATION}>
            <ConfirmationStep
              orgId={orgId}
              state={state}
              dirtyLabels={dirtyLabels}
              sending={sending}
              error={error}
              blockedNonce={blockedNonce}
              onSend={() => void send()}
            />
            <FillStepFooter onBack={() => goTo(previousStep(FillStep.CONFIRMATION))} />
          </FillStepPanel>
        </ToggleTabs>
      </div>
      <UnsavedEditsDialog blocker={blocker} labels={dirtyLabels} />
    </FillJumpProvider>
  )
}
